import type { AuthApi } from './authApi'

/** There is no session, or the server has said it is no longer valid. */
export class SignedOutError extends Error {
  constructor() {
    super('Not signed in.')
    this.name = 'SignedOutError'
  }
}

/** Renew this long before expiry so a request that is already on its way never carries a token that dies in flight. */
export const REFRESH_MARGIN_MS = 60_000

export interface TokenManager {
  /** A database token valid for at least the refresh margin. Rejects with SignedOutError (no or invalid session) or NetworkError (try again later). */
  getJwt(options?: { force?: boolean }): Promise<string>
  /** The last token obtained, even if it has since expired. For display of identity offline, never for authorisation. */
  last(): string | null
  clear(): void
}

export interface TokenManagerOptions {
  api: Pick<AuthApi, 'getToken'>
  getBearer(): Promise<string | null>
  /** The server answered that the session is gone: the caller clears it and tells the app. */
  onInvalid(): Promise<void> | void
  now?: () => number
}

export function createTokenManager({ api, getBearer, onInvalid, now = Date.now }: TokenManagerOptions): TokenManager {
  let cached: { token: string; expiresAt: number } | null = null
  let last: string | null = null
  let inflight: Promise<string> | null = null

  async function fetchToken(): Promise<string> {
    const bearer = await getBearer()
    if (!bearer) throw new SignedOutError()
    const result = await api.getToken(bearer) // NetworkError propagates untouched: offline is not signed out.
    if (result.status === 'invalid') {
      cached = null
      await onInvalid()
      throw new SignedOutError()
    }
    cached = { token: result.token, expiresAt: result.expiresAt }
    last = result.token
    return result.token
  }

  return {
    async getJwt({ force = false } = {}) {
      if (!force && cached && cached.expiresAt - now() > REFRESH_MARGIN_MS) return cached.token
      // One refresh at a time: a burst of sync calls shares a single request.
      inflight ??= fetchToken().finally(() => { inflight = null })
      return inflight
    },
    last: () => last,
    clear() { cached = null; last = null },
  }
}
